/**
 * SPA entry — the Vite equivalent of Next's App Router file tree.
 *
 * The route PATHS are load-bearing: `/app_auth/next-whatsapp`,
 * `/app_auth/next-whatsapp-subaccount`, and `/app_auth/redirect/:id` are
 * registered in the GoHighLevel marketplace as custom-page / OAuth URLs.
 * Renaming one silently breaks installs, so they stay stable.
 */

import React, { Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import "@/app/globals.css";
import { initAnalytics } from "@/lib/analytics";
import AppErrorBoundary from "@/app/AppErrorBoundary";

initAnalytics();

/*
 * Lazy per route.
 *
 * Next code-split by page for free; a Vite SPA does not, and bundling
 * everything together meant someone hitting the OAuth redirect downloaded the
 * entire connections UI, the branding editor and all 30 locale catalogues
 * before it could say "Installed". These are four unrelated surfaces that
 * nobody visits in one session.
 */
const Landing = lazy(() => import("@/app/page"));
const NextWhatsappShell = lazy(() => import("@/app/app_auth/next-whatsapp/page"));
const NextWhatsappSubaccountShell = lazy(
  () => import("@/app/app_auth/next-whatsapp-subaccount/page")
);
const InstallRedirect = lazy(() => import("@/app/app_auth/redirect/[id]/page"));
const RedirectFallback = lazy(() => import("@/app/app_auth/redirect/page"));
const SuperAdminServers = lazy(() => import("@/app/super-admin/servers/page"));

/** Shown only while a route chunk is in flight — deliberately plain. */
function RouteFallback() {
  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-gray-50">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-300 border-t-gray-600" />
    </div>
  );
}

function NotFound() {
  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-gray-50 px-6 text-center">
      <div>
        <h1 className="text-2xl font-semibold text-gray-800">Page not found</h1>
        <p className="mt-2 text-sm text-gray-500">
          That address does not match anything in this app.
        </p>
      </div>
    </div>
  );
}

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <AppErrorBoundary>
      <BrowserRouter>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Landing />} />
            {/* Agency WhatsApp CT custom page. */}
            <Route path="/app_auth/next-whatsapp" element={<NextWhatsappShell />} />
            {/* Free sub-account WhatsApp CT — same Worker / same host. */}
            <Route
              path="/app_auth/next-whatsapp-subaccount"
              element={<NextWhatsappSubaccountShell />}
            />
            {/* The marketplace OAuth redirect. `:id` is the app id. */}
            <Route path="/app_auth/redirect/:id" element={<InstallRedirect />} />
            <Route path="/app_auth/redirect" element={<RedirectFallback />} />
            <Route path="/super-admin/servers" element={<SuperAdminServers />} />
            {/* Next served a trailing slash and the bare path alike; keep that. */}
            <Route path="/index.html" element={<Navigate to="/" replace />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AppErrorBoundary>
  </React.StrictMode>
);
